import React from 'react';
import i18n from '../../i18n';

/* ─────────────────────────────────────────────────────────────
   Indice « M » de réédition sur les documents imprimables du sinistre.
   Le « M » signale un document déjà imprimé et/ou une déclaration
   déjà modifiée - pour distinguer une réédition de l'original.

   « Déjà imprimé » est suivi côté navigateur (localStorage) pour rester
   cohérent avec l'architecture d'impression frontend-only.
   « Déjà modifié » vient du backend (sinistre.dejaModifie via le versioning).
   ───────────────────────────────────────────────────────────── */

const key = (docType, id) => `cbc:printed:${docType}:${id}`;

export const wasPrinted = (docType, id) => {
  if (!id) return false;
  try { return !!localStorage.getItem(key(docType, id)); } catch { return false; }
};

export const markPrinted = (docType, id) => {
  if (!id) return;
  try { localStorage.setItem(key(docType, id), String(Date.now())); } catch { /* stockage indisponible */ }
};

/**
 * Réglages des deux indices, tenus en base (`typeParametre: SYSTEM`) et lus par
 * `useReglagesIndices`. Éteints par défaut : rien ne s'affiche tant qu'un
 * administrateur n'a pas allumé l'un ou l'autre depuis les paramètres.
 */
export const REGLAGES_PAR_DEFAUT = {
  modificationActif: false,
  modificationLibelle: 'M',
  impressionActif: false,
  impressionLibelle: 'I',
};

export const CLES_PARAMETRES = {
  modificationActif: 'INDICE_MODIFICATION_ACTIF',
  modificationLibelle: 'INDICE_MODIFICATION_LIBELLE',
  impressionActif: 'INDICE_IMPRESSION_ACTIF',
  impressionLibelle: 'INDICE_IMPRESSION_LIBELLE',
};

/**
 * Indices à poser sur un document, dans l'ordre d'affichage.
 *
 * Deux faits distincts, deux marques distinctes : une déclaration modifiée et un
 * document déjà tiré ne disent pas la même chose, et confondre les deux sous un
 * seul badge était le défaut d'origine. Ils se cumulent donc quand les deux sont
 * vrais.
 *
 * Un libellé vide retomberait sur un rond muet, que personne ne saurait
 * interpréter : on reprend alors la lettre d'origine.
 */
export const indicesAAfficher = ({ dejaModifie, dejaImprime, reglages = REGLAGES_PAR_DEFAUT }) => {
  const libelle = (saisi, defaut) => (String(saisi ?? '').trim() || defaut);

  return [
    reglages.modificationActif && dejaModifie && {
      cle: 'modification',
      libelle: libelle(reglages.modificationLibelle, REGLAGES_PAR_DEFAUT.modificationLibelle),
      titre: i18n.t('sinistres:indiceReedition.declarationModifiee'),
    },
    reglages.impressionActif && dejaImprime && {
      cle: 'impression',
      libelle: libelle(reglages.impressionLibelle, REGLAGES_PAR_DEFAUT.impressionLibelle),
      titre: i18n.t('sinistres:indiceReedition.documentDejaImprime'),
    },
  ].filter(Boolean);
};

/**
 * Badge d'indice. `libelle` porte la lettre, `title` l'explication au survol.
 * Styles inline pour un rendu fidèle à l'impression.
 */
export function IndiceReedition({ show = true, libelle = 'M', title }) {
  if (!show) return null;
  return (
    <span
      className="indice-reedition no-print-break"
      title={title || i18n.t('sinistres:indiceReedition.reedition')}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '14pt',
        height: '14pt',
        borderRadius: '50%',
        border: '1px solid #d43737',
        color: '#d43737',
        fontSize: '9pt',
        fontWeight: 700,
        lineHeight: 1,
        verticalAlign: 'middle',
        marginLeft: '3pt',
      }}
    >
      {libelle}
    </span>
  );
}

/** Les indices d'un document, prets a poser. */
export function IndicesReedition({ dejaModifie, dejaImprime, reglages }) {
  return indicesAAfficher({ dejaModifie, dejaImprime, reglages })
    .map(({ cle, libelle, titre }) => (
      <IndiceReedition key={cle} libelle={libelle} title={titre} />
    ));
}
